import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMinor, type AdOverview } from '@agency/contracts';
import { Button, EmptyState, Notice, Skeleton, scrollRegion, useToast } from '@agency/ui';
import { ApiClientError, get, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';
import { DemoBadge, providerText } from './common.tsx';
import { DeliveryActions, DeliveryStatus } from './delivery.tsx';
import { CpqlText, DailyChart, FreshnessLine, MetricsStrip, Notes, PeriodSelect, RevenueText, RoasText, SyncProblems, fmtCount, fmtDay, periodRange, type PeriodDays } from './performance.tsx';

/**
 * D087/D088: what the live campaigns cost and returned, for a window, with the freshness of the data stated next to it.
 * Only published campaigns appear here; drafts and reviews live in the Campaigns tab.
 */
export function OverviewTab() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [days, setDays] = useState<PeriodDays>(30);
  const [picked, setPicked] = useState<string | null>(null);
  // After "Refresh numbers" the worker reads the provider in the background; follow the table for a while instead of claiming it finished.
  const [watchUntil, setWatchUntil] = useState(0);
  const range = periodRange(days);
  const q = useQuery<AdOverview, ApiClientError>({
    queryKey: ['ads-overview', days],
    queryFn: () => get(`/ads/overview?from=${range.from}&to=${range.to}`),
    // A pause or resume request is answered by the worker within seconds; follow it so the table never shows a stale "waiting".
    refetchInterval: (query) => (Date.now() < watchUntil || query.state.data?.campaigns.some((c) => c.deliveryChange?.state === 'REQUESTED') ? 3000 : false),
  });
  const refresh = useMutation<{ requested: boolean; message: string }[], ApiClientError, string[]>({
    mutationFn: (ids) => Promise.all(ids.map((connectionId) => post<{ requested: boolean; message: string }>('/ads/sync', { connectionId }))),
    onSuccess: (results) => {
      toast({ tone: results.some((r) => r.requested) ? 'success' : 'info', message: results[0]?.message ?? 'Nothing to refresh.' });
      setWatchUntil(Date.now() + 30_000);
      void qc.invalidateQueries({ queryKey: ['ads-overview'] });
    },
    onError: (e) => toast({ tone: 'error', message: e.message }),
  });

  if (q.isLoading) return <div role="status"><span className="ag-visually-hidden">Loading overview…</span><Skeleton width="100%" height={96} /><div style={{ height: 16 }} /><Skeleton width="100%" height={260} /></div>;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  const o = q.data;
  if (!o) return null;

  const currencies = o.totals.map((t) => t.currency);
  const currency = picked && currencies.includes(picked) ? picked : currencies[0];
  const total = o.totals.find((t) => t.currency === currency);
  const series = o.series.find((s) => s.currency === currency);
  const rows = o.campaigns.filter((c) => c.currency === currency);
  const reportable = o.connections.filter((c) => c.performanceRead && c.status !== 'UNCONFIGURED');

  return (
    <div className="ads-stack">
      <div className="ads-toolbar">
        <PeriodSelect value={days} onChange={setDays} />
        {currencies.length > 1 ? (
          <div className="ads-currency" role="group" aria-label="Currency">
            {currencies.map((c) => <button key={c} type="button" className="view" aria-pressed={c === currency} onClick={() => setPicked(c)}>{c}</button>)}
          </div>
        ) : null}
        {can('ads.edit') && reportable.length ? <Button loading={refresh.isPending} onClick={() => refresh.mutate(reportable.map((c) => c.id))}>Refresh numbers</Button> : null}
      </div>
      <FreshnessLine connections={o.connections} />
      <SyncProblems connections={o.connections} />

      {total && !rows.some((c) => c.dataThrough) ? <Notice tone="info" title="Spend has not been synced yet" live={false}>The provider has not been read for these campaigns, so spend is not shown rather than shown as zero.{can('ads.edit') ? ' Use “Refresh numbers” to ask the worker to read it.' : ''}</Notice> : null}
      {!total || !currency ? (
        <EmptyState title="No live campaigns yet" icon="send">Results appear here once a campaign has been published and the provider has been read. Nothing is shown as zero before then.</EmptyState>
      ) : (
        <>
          <section className="ads-card" aria-labelledby="ads-ov-h">
            <div className="ads-card__head">
              <h2 id="ads-ov-h">{fmtDay(o.range.from)} to {fmtDay(o.range.to)} · {currency}</h2>
              {total.isDemo ? <DemoBadge /> : null}
            </div>
            <MetricsStrip metrics={total.metrics} currency={currency} label={`Results in ${currency}`} spendKnown={rows.some((c) => !!c.dataThrough)} />
            <p className="ag-muted ads-fresh">{total.campaigns} live campaign{total.campaigns === 1 ? '' : 's'} in {currency}. {o.revenueVisible ? 'Revenue is the won deal amount of attributed leads and is never converted between currencies.' : 'Revenue figures need the analytics permission.'}</p>
          </section>
          {series ? (
            <section className="ads-card" aria-label="Daily chart"><DailyChart points={series.points} currency={currency} caption="Is the money spent each day producing leads?" /></section>
          ) : null}
          <section aria-labelledby="ads-camp-h">
            <h2 id="ads-camp-h" style={{ fontSize: 16, margin: '0 0 8px' }}>Live campaigns</h2>
            <div className="ag-table-wrap" {...scrollRegion('Campaign performance table')}>
              <table className="ag-table ads-table ads-table--perf">
                <caption className="ag-visually-hidden">Performance of live campaigns in {currency}, {fmtDay(o.range.from)} to {fmtDay(o.range.to)}</caption>
                <thead><tr><th scope="col">Campaign</th><th scope="col">At provider</th><th scope="col" className="ag-num">Daily budget</th><th scope="col" className="ag-num">Spend</th><th scope="col" className="ag-num">Leads</th><th scope="col" className="ag-num">Qualified</th><th scope="col" className="ag-num">Cost per qualified</th><th scope="col" className="ag-num">Revenue</th><th scope="col" className="ag-num">Return</th><th scope="col">Data</th></tr></thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id}>
                      <td><span className="ads-name"><Link to={`/advertising/${c.id}?tab=performance`}><strong>{c.name}</strong></Link><span className="ag-muted">{[c.accountName, providerText(c), c.landingName, c.marketCode].filter(Boolean).join(' · ')}</span>{c.isDemo ? <DemoBadge /> : null}</span></td>
                      <td>
                        <DeliveryStatus deliveryStatus={c.deliveryStatus} change={c.deliveryChange} />
                        <div className="ads-actions" style={{ marginTop: 4 }}><DeliveryActions campaign={c} size="sm" /></div>
                      </td>
                      <td className="ag-num">{formatMinor(c.dailyBudgetMinor, c.currency)}</td>
                      <td className="ag-num">{formatMinor(c.metrics.spendMinor, c.currency)}</td>
                      <td className="ag-num">{fmtCount(c.metrics.leads)}</td>
                      <td className="ag-num">{fmtCount(c.metrics.qualifiedLeads)}</td>
                      <td className="ag-num"><CpqlText metrics={c.metrics} currency={c.currency} /></td>
                      <td className="ag-num"><RevenueText metrics={c.metrics} /></td>
                      <td className="ag-num"><RoasText metrics={c.metrics} /></td>
                      <td>{c.dataThrough ? <span className="ag-cell-2"><span>through {fmtDay(c.dataThrough)}</span></span> : <span className="ag-muted">Not synced yet</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      <Notes notes={o.notes} />
    </div>
  );
}
